import {useState} from 'react'
import {useTranslation} from 'react-i18next'
import {Link} from 'react-router-dom'
import {Button} from '@/components/ui/button'
import {Tag} from '@/components/ui/tag'
import {MicroLabel} from '@/components/period-selector'
import {ConfirmDialog} from '@/components/confirm-dialog'
import {translateApiError} from '@/lib/errors'
import {
  downloadPerfilExcel,
  usePerfilCoverage,
  useReSolicitarPerfilTodos,
} from '@/hooks/salud/perfil_riesgo/use-perfil-agregados'

/** v4.2 spec D4. The parent renders it only with `canOperate`: the endpoints are operador-only. */
export function PerfilSociodemograficoCard({businessId}: {businessId: string}) {
  const {t} = useTranslation()
  // The endpoints always report the current year, not the period the SG-SST selector is viewing.
  const year = new Date().getFullYear()
  const {data: coverage} = usePerfilCoverage(businessId)
  const reSolicitar = useReSolicitarPerfilTodos(businessId)
  const [confirming, setConfirming] = useState(false)
  const [result, setResult] = useState<number | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [downloadError, setDownloadError] = useState<string | null>(null)

  if (!coverage) return null

  const complete = coverage.active_count > 0 && coverage.sealed_count >= coverage.active_count
  const hasMissing = coverage.sealed_count < coverage.active_count

  function confirmReSolicitar() {
    setError(null)
    reSolicitar.mutate(undefined, {
      onSuccess: (data) => {
        setResult(data.re_solicitados)
        setConfirming(false)
      },
      onError: (err) => {
        setError(translateApiError(err))
        setConfirming(false)
      },
    })
  }

  async function handleDownload() {
    setDownloadError(null)
    try {
      await downloadPerfilExcel(businessId)
    } catch (err) {
      setDownloadError(translateApiError(err))
    }
  }

  return (
    <div className="mt-2 space-y-2 rounded-lg bg-secondary/40 px-3 py-2.5" data-testid="perfil-sociodemografico-card">
      <div className="flex flex-wrap items-baseline gap-x-2 gap-y-1">
        <MicroLabel>{t('perfilAgregados.cardEyebrow')}</MicroLabel>
        <span className="font-mono text-sm font-medium text-foreground" data-testid="perfil-coverage-count">
          {t('perfilAgregados.cardTitle', {sealed: coverage.sealed_count, active: coverage.active_count})}
        </span>
        {complete && <Tag tone="green">{t('sgsst.status.cumple')}</Tag>}
        <span className="text-xs text-muted-foreground">
          {complete ? t('perfilAgregados.cardComplete', {year}) : t('perfilAgregados.cardSubtitle', {year})}
        </span>
      </div>

      <div className="flex flex-wrap items-center gap-2">
        <Button asChild size="sm" variant="outline">
          <Link to={`/businesses/${businessId}/sgsst/perfil-agregados`} data-testid="perfil-view-aggregates">
            {t('perfilAgregados.viewAggregates')}
          </Link>
        </Button>
        <Button size="sm" variant="outline" data-testid="perfil-export" onClick={handleDownload}>
          {t('perfilAgregados.export')}
        </Button>
        {hasMissing && (
          <Button
            size="sm"
            data-testid="perfil-resolicitar"
            disabled={reSolicitar.isPending}
            onClick={() => setConfirming(true)}>
            {reSolicitar.isPending ? t('perfilAgregados.resolicitarPending') : t('perfilAgregados.resolicitar')}
          </Button>
        )}
      </div>

      {result !== null && (
        <p className="text-xs text-muted-foreground" data-testid="perfil-resolicitar-result">
          {result > 0 ? t('perfilAgregados.resolicitarDone', {count: result}) : t('perfilAgregados.resolicitarNone')}
        </p>
      )}
      {error && <p className="text-xs text-destructive">{error}</p>}
      {downloadError && <p className="text-xs text-destructive">{downloadError}</p>}

      <ConfirmDialog
        open={confirming}
        onOpenChange={setConfirming}
        title={t('perfilAgregados.resolicitarTitle')}
        description={t('perfilAgregados.resolicitarBody')}
        confirmLabel={t('perfilAgregados.resolicitarConfirm')}
        cancelLabel={t('common.cancel')}
        onConfirm={confirmReSolicitar}
        pending={reSolicitar.isPending}
      />
    </div>
  )
}
